import { useMeQuery } from '../generated/graphql'
import { needsPostRegistrationSetup } from '../utils'
import useIsSubscribed from './useIsSubscribed'

/**
 * Post-registration setup gate.
 *
 * Resolves, from server truth, whether a guided (PIA) onboarding customer still
 * needs to complete the pay gate before they can use the platform. Used to
 * redirect such customers to the pay gate and to block the rest of the app until
 * setup is finished.
 *
 * Robustness (this gates every authenticated route, so a false positive would
 * lock out a paying customer):
 *  - Scoped strictly to the automated signup flow (legacy / invited accounts are
 *    never gated).
 *  - Belt-and-suspenders: a customer with an active/trialing subscription is
 *    never gated, even if their onboarding flags were somehow stale.
 *  - Errs OPEN on missing data, and only reports `loading` while it genuinely
 *    cannot decide yet — so navigation never flashes a loader and a data hiccup
 *    can never wrongly redirect a paying customer.
 */
const usePostRegistrationGate = () => {
  const { data, loading: meLoading } = useMeQuery()
  const { isSubscribed, loading: subLoading } = useIsSubscribed()

  const hasUser = !!data?.myUser
  const needsSetupByOnboarding = needsPostRegistrationSetup(data?.myUser?.attributes)

  return {
    // Hold only while we truly cannot decide yet: the user isn't loaded, or the
    // onboarding flags say "needs setup" but we're still confirming there's no
    // active subscription. Paying customers (flags already clear) never wait on
    // the subscription query.
    loading: (meLoading && !hasUser) || (needsSetupByOnboarding && subLoading),
    needsSetup: needsSetupByOnboarding && !isSubscribed
  }
}

export default usePostRegistrationGate
